import React, {
  RefObject,
  useMemo,
  useRef,
} from 'react';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import {
  PayerEntryConfigDTO,
} from 'dtos';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import {
  useUpsertElectronicPayerConfig,
} from 'views/EMR/Settings/hooks';
import Button from 'pages/Dashboard/components/Button';
import Input from 'pages/Dashboard/components/Input';
import InsuranceLookup from 'pages/Dashboard/components/InsuranceLookup';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Tooltip from 'core/components/Tooltip';
import PinIcon from 'core/icons/pin';
import {
  useNotify,
} from 'core/hooks';

type Props = {
  data?: PayerEntryConfigDTO;
  onClose: () => void;
};

const payerKeys = ['payerName', 'payerId', 'payerEntryId'] as const;
type PayerFields = typeof payerKeys[number];
type PayerConfigForm = Partial<
    Omit<PayerEntryConfigDTO, PayerFields>>
  & { payer?: Pick<PayerEntryConfigDTO, PayerFields> };

export default function ElectronicPayerForm({
  data,
  onClose,
}: Props) {
  const insuranceLookupRef = useRef<HTMLInputElement>(null);
  const isNew = isNil(data);

  const { mutateAsync: upsertElectronicPayerConfig } = useUpsertElectronicPayerConfig();
  const notify = useNotify();

  const formProps = useForm<PayerConfigForm>({
    mode: 'onChange',
    defaultValues: isNil(data)
      ? { isPinned: true }
      : {
        ...omit(data, payerKeys),
        payer: pick(data, payerKeys),
      },
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isValid, isSubmitting },
  } = formProps;

  const payer = watch('payer');

  const onSubmit = async (formData: PayerConfigForm) => {
    const { payer, ...data } = formData;
    try {
      await upsertElectronicPayerConfig({ ...data, ...payer });
      notify({
        message: `Payer Configuration has been ${isNew ? 'created' : 'updated'}.`,
        variant: 'success',
      });
      settingsEventChannel.emit('onPayerConfigChange');
      onClose?.();
    } finally {
      reset(formData, { keepIsSubmitted: false });
    }
  };

  const submitButtonText = useMemo(() => getSubmitButtonText(
    isNew,
    isSubmitting,
  ), [isNew, isSubmitting]);

  const hasPayer = !isNil(payer);

  return (
    <FormProvider {...formProps}>
      <div className="flex flex-col gap-form-row">
        <div className="flex gap-form-field items-end">
          <Controller
            name="payer"
            control={control}
            rules={{ required: 'Payer is required' }}
            render={({ field, fieldState: { error } }) => (
              <InsuranceLookup
                {...field}
                onChange={(value) => {
                  field.onChange(pick(value, payerKeys));
                }}
                label="Payer"
                required
                error={!isNil(error)}
                id="payer-name"
                className="flex-1 !mb-0"
                inputRef={insuranceLookupRef as RefObject<HTMLInputElement>}
                searchType="Electronic"
                toggleFavorites={false}
              />
            )}
          />
          {hasPayer && (
          <Controller
            name="isPinned"
            control={control}
            render={({ field: { value: isPinned, onChange } }) => (
              <Tooltip title={isPinned ? 'Un-Pin Payer' : 'Pin Payer'}>
                <IconButton
                  size="small"
                  color="info"
                  className={clsx({
                    '!text-blue-500': isPinned,
                    '!text-gray-400': !isPinned,
                  })}
                  onClick={() => { onChange(!isPinned); }}
                >
                  <PinIcon
                    className={clsx('!w-5 !h-5 !text-inherit', {
                      'rotate-45': !isPinned,
                    })}
                  />
                </IconButton>
              </Tooltip>
            )}
          />
          )}
        </div>
        {hasPayer && (
          <div className="flex w-full">
            <Controller
              name="submitterId"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  showErrorText={false}
                  id="submitter-id"
                  type="text"
                  className="w-full"
                  value={field.value ?? ''}
                  label="Submitter ID"
                />
              )}
            />
          </div>
        )}
      </div>
      <div className="flex flex-row-reverse justify-between gap-3 mt-4">
        <DisabledFormTooltip>
          <Button
            variant="contained"
            type="submit"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || isSubmitting}
          >
            {submitButtonText}
          </Button>
        </DisabledFormTooltip>
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Close
        </Button>
      </div>
    </FormProvider>
  );
}
